/**
 * Microsoft Teams alert-destination data: whether Teams is usable for the org
 * (`useTeamsAvailable`) and the channels the bot can post to, shaped as
 * `ComboBox` options (`useTeamsChannels`). Mirrors `useSlackChannels`.
 *
 * @module features/notifications/hooks/useTeamsChannels
 */
import { useQuery } from '@tanstack/react-query';
import { API_URL } from '../../../config';
import { toApiError } from '../../../lib/api-error';

type TeamsConnection = { isFlagEnabled: boolean; installCount: number };

export type TeamsChannel = {
  channelRef: string;
  name: string;
  parentName?: string;
  isPrivate: boolean;
  isMember: boolean;
};

/** Shares the `['teams-connection']` cache with `TeamsSettingsSection`. */
export function useTeamsAvailable() {
  const query = useQuery({
    queryKey: ['teams-connection'],
    queryFn: async (): Promise<TeamsConnection> => {
      const res = await fetch(`${API_URL}/integrations/teams/connection`, {
        credentials: 'include',
      });
      if (!res.ok) throw await toApiError(res);
      return res.json();
    },
  });
  return {
    teamsAvailable: query.data?.isFlagEnabled === true && query.data.installCount > 0,
    /** Still loading or errored — `teamsAvailable` reads `false` in both, which is not "confirmed unavailable". */
    teamsUnknown: query.isLoading || query.isError,
  };
}

export function useTeamsChannels(scope: string, opts?: { enabled?: boolean }) {
  const query = useQuery({
    queryKey: ['teams-channels', scope],
    enabled: opts?.enabled ?? true,
    queryFn: async (): Promise<{ ok: boolean; channels?: TeamsChannel[] }> => {
      const res = await fetch(`${API_URL}/integrations/teams/channels`, {
        credentials: 'include',
      });
      if (!res.ok) throw await toApiError(res);
      return res.json();
    },
  });

  const channelOptions =
    query.data?.channels
      ?.filter((channel) => !channel.isPrivate || channel.isMember)
      .map((channel) => ({
        id: channel.channelRef,
        label: channel.parentName ? `${channel.parentName} › ${channel.name}` : channel.name,
      })) ?? [];

  return { ...query, channelOptions };
}
